import { useState } from 'react'
import { Clapperboard, Loader2, Play, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { api } from '@/lib/api'
import {
  useFollowupPending,
  useMarkFollowupSeen,
  useSkipFollowupVideo,
} from '@/hooks/useFollowupVideos'
import VideoPlaybackModal from './VideoPlaybackModal'
import type { FollowupVideoPlayback } from '@/types/api'

/**
 * O alerta de video novo (spec 05, R5): "voce tem video sobre XXX para assistir".
 *
 * Le `GET /followup_videos/pending`, que NAO refaz casamento nenhum — a pergunta ali e
 * sobre o passado do aluno ("o que ja foi oferecido e continua pendente"). Um video
 * `seen` ou `skipped` some da lista no servidor, e e assim que "nao reaparece para video
 * visto ou pulado" se sustenta sem estado local nenhum.
 *
 * Escopo declarado: apenas `/app/*`, porque e montado no DashboardLayout. NAO aparece em
 * /onboarding (que tem o painel proprio da conclusao) nem nas areas de admin e instrutor.
 * E decisao, nao omissao.
 */

/** Dispensa VALIDA POR SESSAO. Nao e persistencia de estado: o estado real e o status. */
const DISMISSED_KEY = 'medhunter-followup-alert-dismissed'

/**
 * `sessionStorage` lanca em janela anonima e com cookies de terceiros bloqueados. Uma
 * excecao aqui derrubaria o layout inteiro do app, entao os dois lados sao tolerantes:
 * ler falha como "nao dispensado", escrever falha em silencio.
 */
function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === 'true'
  } catch {
    return false
  }
}

function writeDismissed(): void {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, 'true')
  } catch {
    // Sem sessionStorage o banner volta na proxima navegacao. E o comportamento menos
    // ruim: ele continua dispensavel na tela, so nao lembra disso.
  }
}

export default function FollowupVideoAlert() {
  const [dismissed, setDismissed] = useState(readDismissed)
  const [playback, setPlayback] = useState<FollowupVideoPlayback | null>(null)
  const [isOpening, setIsOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { data } = useFollowupPending(!dismissed)
  const markSeen = useMarkFollowupSeen()
  const skipVideo = useSkipFollowupVideo()

  const videos = data?.videos ?? []
  const first = videos[0]

  const playbackModal = playback ? (
    <VideoPlaybackModal playback={playback} onClose={() => setPlayback(null)} />
  ) : null

  /*
    O player sobrevive ao banner. `watch()` marca `seen` no mesmo instante em que abre o
    modal, o `useFollowupStateMutation` invalida FOLLOWUP_PENDING_KEY e a lista volta sem
    aquele video — se ele era o unico pendente, `first` some. Com um `return null` aqui o
    modal (que e irmao do banner, mais abaixo no mesmo componente) desapareceria junto,
    fechando o video no primeiro segundo de reproducao.
  */
  if (dismissed || !first) return playbackModal

  const dismiss = () => {
    setDismissed(true)
    writeDismissed()
  }

  const watch = async () => {
    setIsOpening(true)
    setError(null)

    try {
      const result = await api.followupVideos.playback(first.id)
      setPlayback(result)
      markSeen.mutate(first.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível abrir o vídeo agora.')
    } finally {
      setIsOpening(false)
    }
  }

  /**
   * "Nao quero ver esses videos" pula TODOS os pendentes, um a um: `skip` e por video,
   * e nao ha rota de lote. Sao no maximo cinco (o teto do endpoint), entao o custo e o
   * mesmo de uma tela com cinco botoes de pular.
   */
  const skipAll = () => {
    videos.forEach((video) => skipVideo.mutate(video.id))
    setDismissed(true)
  }

  const others = videos.length - 1

  return (
    <>
      <div className="mb-4 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-primary-500/30 dark:bg-primary-500/10">
        <Clapperboard className="mt-0.5 h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />

        <div className="min-w-0 flex-1">
          <p className="text-sm text-neutral-800 dark:text-neutral-100">
            Você tem vídeo sobre{' '}
            <span className="font-semibold">{first.subject}</span> para assistir
            {others > 0 && (
              <span className="text-neutral-500 dark:text-neutral-400">
                {' '}e mais {others} {others === 1 ? 'vídeo' : 'vídeos'}
              </span>
            )}
            .
          </p>

          {error && <p className="mt-1 text-xs text-red-500">{error}</p>}

          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button variant="primary" size="sm" onClick={() => void watch()} disabled={isOpening}>
              {isOpening ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Assistir
            </Button>
            <button
              type="button"
              onClick={skipAll}
              className="text-xs text-neutral-500 underline-offset-2 hover:underline dark:text-neutral-400"
            >
              Não quero ver esses vídeos
            </button>
          </div>
        </div>

        {/* O X dispensa so a SESSAO: o video continua pendente e o aviso volta amanha.
            Quem quer que ele nao volte usa o link acima, que grava `skipped`. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar aviso"
          className="shrink-0 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {playbackModal}
    </>
  )
}
